import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { capitalize } from 'lodash-es';

export const IncentivesConfigPropType = PropTypes.shape({
  makeSlug: PropTypes.string,
  modelSlug: PropTypes.string,
  programType: PropTypes.string,
  engineType: PropTypes.string,
  activeDate: PropTypes.instanceOf(Date),
  pageNum: PropTypes.number,
  displayMode: PropTypes.string,
  sortBy: PropTypes.string,
  transactionType: PropTypes.string,
  vehCondition: PropTypes.string,
});

export const ELECTRIC_CAR_INCENTIVES_CONTENT_PATH = '/electric-car/tax-credits-rebates-incentives';
export const MATCH_LIST_CREATIVE_ID = 'incentives-match-list';
export const ELECTRIC_CAR_INCENTIVES_CARDS_ID = 'electric-car-incentives-cards';
export const ALL_VALUE = 'all';
export const ALL_VALUE_TEXT_MAP = {
  MAKE: 'All Makes',
  MODEL: 'All Models',
  REBATE: 'All Rebates',
};

export const FORMS_HEADINGS = {
  MAKE: 'Make',
  MODEL: 'Model',
  ALL_FILTERS: 'All filters',
  REBATE: 'Rebate Type',
  SORT_BY: 'Sort by',
  ENGINE_TYPES: 'Engine type',
  VEHICLE_CONDITION: 'Vehicle Condition',
  TRANSACTION_TYPE: 'Purchase Method',
};

export const DISPLAY_MODE = {
  INCENTIVES: 'incentives',
  MODEL_YEARS: 'models',
};

export const DISPLAY_MODES = [DISPLAY_MODE.MODEL_YEARS, DISPLAY_MODE.INCENTIVES];
export const INCENTIVES_MMY = 'incentives-mmy';

export const PAGE_SIZE = {
  [DISPLAY_MODE.MODEL_YEARS]: 15,
  [DISPLAY_MODE.INCENTIVES]: 16,
  [INCENTIVES_MMY]: 30,
};

export const HERO_IMAGE = '/img/ev-incentives-tool/house-car-group.svg';

export const CARDS_LIST_CREATIVE_ID = {
  [DISPLAY_MODE.MODEL_YEARS]: 'incentives-model-list',
  [DISPLAY_MODE.INCENTIVES]: 'incentives-list',
};
export const SORT_LIST_CREATIVE_ID = {
  [DISPLAY_MODE.INCENTIVES]: 'incentives-list-sort',
  [DISPLAY_MODE.MODEL_YEARS]: 'incentives-model-list-sort',
};

export const TRANSACTION_TYPES = ['CASH', 'FINANCE', 'LEASE'];

export const TRANSACTION_TYPES_OPTIONS = TRANSACTION_TYPES.map(transactionType => ({
  text: capitalize(transactionType),
  title: transactionType,
  hideValue: true,
}));

export const CONDITION_TYPES = {
  NEW: 'NEW',
  USED: 'USED',
};

export const VEHICLE_CONDITION_OPTIONS = Object.values(CONDITION_TYPES).map(conditionType => ({
  text: capitalize(conditionType),
  title: conditionType,
  hideValue: true,
}));

export const ENGINE_TYPES = [
  { text: 'Electric', title: 'electric', hideValue: true },
  { text: 'Plug-in Hybrid', title: 'plug-in hybrid', hideValue: true },
];

export const ENGINE_TYPE_TITLE = {
  electric: 'EV',
  'plug-in hybrid': 'plug-in hybrid',
};

export const SORTING_NAMES = {
  YEAR: 'Vehicle Year',
  DISCOUNT: 'Discount Percent',
  NAME: 'Vehicle Name',
  TOTAL_AMOUNT: 'Vehicle Rebate Total',
  AMOUNT: 'Rebate Amount',
  REBATE_NAME: 'Rebate Name',
  EXPIRATION_DATE: 'Expiration date',
};
export const SORTING_CONFIG = {
  [DISPLAY_MODE.INCENTIVES]: {
    [SORTING_NAMES.AMOUNT]: { topIncentiveByRebateAmount: 'DESC' },
    [SORTING_NAMES.REBATE_NAME]: { subtype: 'ASC' },
    [SORTING_NAMES.EXPIRATION_DATE]: { topIncentiveByRebateEndDate: 'ASC' },
  },
  [DISPLAY_MODE.MODEL_YEARS]: {
    [SORTING_NAMES.TOTAL_AMOUNT]: { maxCalcNonChargingSum: 'DESC' },
    [SORTING_NAMES.DISCOUNT]: { maxCalcNonChargingPercent: 'DESC' },
    [SORTING_NAMES.NAME]: { makeModelYear: 'ASC' },
    [SORTING_NAMES.YEAR]: { year: 'DESC' },
  },
  [INCENTIVES_MMY]: {
    [SORTING_NAMES.AMOUNT]: { topIncentiveByRebateAmount: 'DESC' },
  },
};
export const SORTING = {
  [DISPLAY_MODE.INCENTIVES]: Object.keys(SORTING_CONFIG[DISPLAY_MODE.INCENTIVES]).map(name => ({
    text: name,
    title: name,
    hideValue: true,
  })),
  [DISPLAY_MODE.MODEL_YEARS]: Object.keys(SORTING_CONFIG[DISPLAY_MODE.MODEL_YEARS]).map(name => ({
    text: name,
    title: name,
    hideValue: true,
  })),
};

export const FILTER_QUERY_PARAMS = {
  PAGENUM: 'pagenumber',
  MAKE: 'make',
  MODEL: 'model',
  PROGRAM_TYPE: 'programTypes',
  ACTIVE_DATE: 'buyDate',
  DISPLAY_MODE: 'viewBy',
  SORT_BY: 'sortBy',
  ENGINE_TYPE: 'engineType',
  TRANSACTION_TYPE: 'purchaseMethod',
  USER_MSRP: 'msrp',
  VEHICLE_CONDITION: 'vehCondition',
};

export const ENGINE_TYPE_DEFAULT_VALUE = 'electric';

// For ACTIVE_DATE use 'defaultActiveDate' model path
export const DEFAULT_FILTERS = {
  [FILTER_QUERY_PARAMS.MAKE]: ALL_VALUE,
  [FILTER_QUERY_PARAMS.MODEL]: ALL_VALUE,
  [FILTER_QUERY_PARAMS.PROGRAM_TYPE]: [],
  [FILTER_QUERY_PARAMS.DISPLAY_MODE]: DISPLAY_MODE.MODEL_YEARS,
  [FILTER_QUERY_PARAMS.ENGINE_TYPE]: ENGINE_TYPE_DEFAULT_VALUE,
  [FILTER_QUERY_PARAMS.TRANSACTION_TYPE]: TRANSACTION_TYPES[0],
  [FILTER_QUERY_PARAMS.USER_MSRP]: null,
  [FILTER_QUERY_PARAMS.VEHICLE_CONDITION]: CONDITION_TYPES.NEW,
};

export const DEFAULT_NAVIGATION_PARAMS = {
  [DISPLAY_MODE.MODEL_YEARS]: {
    [FILTER_QUERY_PARAMS.PAGENUM]: 1,
    [FILTER_QUERY_PARAMS.SORT_BY]: SORTING_NAMES.TOTAL_AMOUNT,
  },
  [DISPLAY_MODE.INCENTIVES]: {
    [FILTER_QUERY_PARAMS.PAGENUM]: 1,
    [FILTER_QUERY_PARAMS.SORT_BY]: SORTING_NAMES.AMOUNT,
  },
};

const ELECTRIC_CAR_INCENTIVES_GRAPHQL_FRAGMENTS = {
  rebateOverridesByEligibilitySegmentFieldsWithoutFactors: gql`
    fragment rebateOverridesByEligibilitySegmentFieldsWithoutFactors on Incentive {
      rebateOverridesByEligibility {
        rebateAmount
        rebateLevel
      }
    }
  `,
  rebateOverridesByEligibilitySegmentFields: gql`
    fragment rebateOverridesByEligibilitySegmentFields on Incentive {
      rebateOverridesByEligibility {
        rebateAmount
        eligibilityFactor {
          ... on EligibilityFactor {
            qualifier
            id
            taxFilingStatus
            maxIncome
            householdSize
            assistanceParticipant
          }
        }
      }
    }
  `,
  incentiveFields: gql`
    fragment incentiveFields on Incentive {
      programType
      subtype
      subtypeId
      rebateAmount
      endDate
      restrictions
      id
      type
      transactionTypes
      grantor
      updateDate
      primary
      compatibleIncentiveIds
    }
  `,

  incentivesSegmentFields: gql`
    fragment incentivesSegmentFields on IncentiveList {
      incentiveAggregations {
        grantorAndSubtypeCount
        byGrantorAndSubtype(page: $page, sortBy: $sortBy) {
          topIncentiveByRebate {
            ...incentiveFields
            ...rebateOverridesByEligibilitySegmentFieldsWithoutFactors
          }
        }
      }
    }
  `,
  incentivesWizardSegmentFields: gql`
    fragment incentivesWizardSegmentFields on IncentiveList {
      incentiveAggregations {
        grantorAndSubtypeCount
        byGrantorAndSubtype(page: $page, sortBy: $sortBy) {
          topIncentiveByRebate {
            ...incentiveFields
            ...rebateOverridesByEligibilitySegmentFields
          }
        }
      }
    }
  `,
  incentiveAggregationsSegmentFields: gql`
    fragment incentiveAggregationsSegmentFields on IncentiveList {
      incentiveAggregations {
        modelYearCount
        models
        makes
        engineTypes
        programTypes
        byModelYear(page: $page, sortBy: $sortBy) {
          maxCalcNonChargingPercent
          maxCalcIncentiveSum
          maxCalcChargingSum
          maxCalcNonChargingSum
          maxCalcIncentiveStyleId
          modelYear {
            id
            makeName
            modelName
            makeSlug
            modelSlug
            year
            publicationStates
            segmentRatings {
              rating
            }
            styleList {
              styleAggregations {
                maxEdmundsElectricityRange
                maxEPAElectricityRange
                priceRange {
                  min
                  max
                }
              }
            }
          }
        }
      }
    }
  `,
};

export const QUERIES = {
  INCENTIVE_AGGREGATIONS: 'incentiveAggregations',
  INCENTIVES: 'incentives',
  WIZARD_INCENTIVES: 'wizardIncentives',
};
export const ELECTRIC_CAR_INCENTIVES_GRAPHQL_QUERY = {
  [QUERIES.INCENTIVE_AGGREGATIONS]: gql`
    ${ELECTRIC_CAR_INCENTIVES_GRAPHQL_FRAGMENTS.incentiveAggregationsSegmentFields}
    query (
      $incentiveFilters: IncentiveFilters!
      $eligibilityFactors: [EligibilityFactorFilter!]
      $page: PageRequest!
      $sortBy: IncentiveAggregationByModelYearSort!
    ) {
      incentives(incentiveFilters: $incentiveFilters, eligibilityFactors: $eligibilityFactors) {
        ...incentiveAggregationsSegmentFields
      }
    }
  `,
  [QUERIES.INCENTIVES]: gql`
    ${ELECTRIC_CAR_INCENTIVES_GRAPHQL_FRAGMENTS.rebateOverridesByEligibilitySegmentFieldsWithoutFactors}
    ${ELECTRIC_CAR_INCENTIVES_GRAPHQL_FRAGMENTS.incentiveFields}
    ${ELECTRIC_CAR_INCENTIVES_GRAPHQL_FRAGMENTS.incentivesSegmentFields}
    query (
      $incentiveFilters: IncentiveFilters!
      $eligibilityFactors: [EligibilityFactorFilter!]
      $page: PageRequest!
      $sortBy: IncentiveAggregationByGrantorAndSubtypeSort!
    ) {
      incentives(incentiveFilters: $incentiveFilters, eligibilityFactors: $eligibilityFactors) {
        ...incentivesSegmentFields
      }
    }
  `,
  [QUERIES.WIZARD_INCENTIVES]: gql`
    ${ELECTRIC_CAR_INCENTIVES_GRAPHQL_FRAGMENTS.rebateOverridesByEligibilitySegmentFields}
    ${ELECTRIC_CAR_INCENTIVES_GRAPHQL_FRAGMENTS.incentiveFields}
    ${ELECTRIC_CAR_INCENTIVES_GRAPHQL_FRAGMENTS.incentivesWizardSegmentFields}
    query (
      $incentiveFilters: IncentiveFilters!
      $page: PageRequest!
      $sortBy: IncentiveAggregationByGrantorAndSubtypeSort!
    ) {
      incentives(incentiveFilters: $incentiveFilters) {
        ...incentivesWizardSegmentFields
      }
    }
  `,
};
